/**
 * PlatformRevenuePage.tsx — Platform-wide revenue and fee analytics
 *
 * Shows platform operator revenue: fees collected across all tenants,
 * fee thresholds, per-tenant revenue breakdown.
 *
 * @since 2026-06-19 (SaaS conversion — Platform Admin)
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import PlatformLayout from '../components/PlatformLayout';
import StatCard from '../components/StatCard';

interface TenantRevenue {
  tenant_id: string;
  tenant_name: string;
  total_processed_cents: number;
  platform_fee_cents: number;
  fee_pct: number;
  status: string;
}

export default function PlatformRevenuePage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'revenue', 'detail'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/platform/revenue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json() as Promise<{
        stats: {
          total_tenants: number;
          active_tenants: number;
          total_processed_cents: number;
          total_fees_cents: number;
          tenants: TenantRevenue[];
        };
      }>;
    },
    enabled: !!token,
  });

  const stats = data?.stats;

  return (
    <PlatformLayout title="Platform Revenue">
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <StatCard
            label="Total Processed"
            value={isLoading ? '...' : `$${((stats?.total_processed_cents ?? 0) / 100).toLocaleString()}`}
          />
          <StatCard
            label="Platform Fees Collected"
            value={isLoading ? '...' : `$${((stats?.total_fees_cents ?? 0) / 100).toLocaleString()}`}
          />
          <StatCard
            label="Active Tenants"
            value={isLoading ? '...' : String(stats?.active_tenants ?? 0)}
          />
          <StatCard
            label="Total Tenants"
            value={isLoading ? '...' : String(stats?.total_tenants ?? 0)}
          />
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {String(error)}
          </div>
        )}

        {/* Per-tenant breakdown */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">Revenue by Tenant</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-6 py-3">Tenant</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Total Processed</th>
                  <th className="px-6 py-3 text-right">Fee %</th>
                  <th className="px-6 py-3 text-right">Fees Collected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading && (
                  <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-400">Loading...</td></tr>
                )}
                {!isLoading && (!stats?.tenants || stats.tenants.length === 0) && (
                  <tr><td colSpan={5} className="px-6 py-8 text-center text-slate-400">No tenant revenue data yet.</td></tr>
                )}
                {stats?.tenants?.map((t) => (
                  <tr key={t.tenant_id} className="hover:bg-slate-50">
                    <td className="px-6 py-3 font-medium text-slate-900">{t.tenant_name}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                        t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right">${(t.total_processed_cents / 100).toLocaleString()}</td>
                    <td className="px-6 py-3 text-right">{(t.fee_pct * 100).toFixed(2)}%</td>
                    <td className="px-6 py-3 text-right">${(t.platform_fee_cents / 100).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
}
